import { useAuthStore } from "../../store/auth";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTags } from "../../hooks/useTags";
import {
  bulkAddTag,
  bulkArchive,
  bulkDelete,
  bulkDeletePermanently,
  bulkExport,
  bulkPin,
  bulkRestore,
  bulkUnarchive,
} from "../../lib/bulkActions";
import type { ExportFormat } from "../../lib/export";
import { useSelectionStore } from "../../store/selection";
import { useIsPro } from "../../store/subscription";
import { useUIStore } from "../../store/ui";
import { Button } from "../ui/Button";
import { ProTag } from "../ui/ProTag";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { ToolbarPill, ToolbarSeparator } from "../ui/ToolbarPill";
import { Icon } from "@/components/ui/Icon";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { getNote } from "../../lib/db";
import { isSharedWithMe } from "../../lib/sharing";

interface BulkActionsBarProps {
  variant?: "default" | "trash" | "archive";
  onDeleteActiveNote?: () => void;
}

export function BulkActionsBar({ variant = "default", onDeleteActiveNote }: BulkActionsBarProps) {
  const { t } = useTranslation();
  const isPro = useIsPro();
  const openPaywall = useUIStore((s) => s.openPaywall);
  const selectedIds = useSelectionStore((s) => s.selectedIds);
  const clearSelection = useSelectionStore((s) => s.clearSelection);
  const count = selectedIds.size;
  const ids = Array.from(selectedIds);

  const allTags = useTags();
  const [tagFilterText, setTagFilterText] = useState("");

  const filteredTags = allTags.filter((t) =>
    t.name.toLowerCase().includes(tagFilterText.toLowerCase()),
  );

  const handlePin = useCallback(() => {
    bulkPin(ids, true);
    clearSelection();
  }, [ids, clearSelection]);

  const handleUnpin = useCallback(() => {
    bulkPin(ids, false);
    clearSelection();
  }, [ids, clearSelection]);

  const doDelete = useCallback(() => {
    bulkDelete(ids, useAuthStore.getState().user?.id);
    onDeleteActiveNote?.();
    clearSelection();
  }, [ids, clearSelection, onDeleteActiveNote]);

  // Notes shared with this account are left, not trashed (PLAN.md D5): say
  // so first, with how many.
  const [leaveCount, setLeaveCount] = useState(0);
  const handleDelete = useCallback(() => {
    const me = useAuthStore.getState().user?.id;
    const shared = ids.filter((id) => isSharedWithMe(getNote(id), me)).length;
    if (shared > 0) setLeaveCount(shared);
    else doDelete();
  }, [ids, doDelete]);

  const handleRestore = useCallback(() => {
    bulkRestore(ids);
    clearSelection();
  }, [ids, clearSelection]);

  const handleArchive = useCallback(() => {
    bulkArchive(ids);
    onDeleteActiveNote?.();
    clearSelection();
  }, [ids, clearSelection, onDeleteActiveNote]);

  const handleUnarchive = useCallback(() => {
    bulkUnarchive(ids);
    clearSelection();
  }, [ids, clearSelection]);

  const handleDeletePermanently = useCallback(() => {
    bulkDeletePermanently(ids);
    onDeleteActiveNote?.();
    clearSelection();
  }, [ids, clearSelection, onDeleteActiveNote]);

  const handleExport = useCallback(
    async (format: ExportFormat) => {
      // Bulk export is entirely a Caliu Pro feature.
      if (!isPro) {
        openPaywall("bulk_export");
        return;
      }
      await bulkExport(ids, format);
      clearSelection();
    },
    [ids, clearSelection, isPro, openPaywall],
  );

  const handleAddTag = useCallback(
    (tagId: string) => {
      bulkAddTag(ids, tagId);
      clearSelection();
    },
    [ids, clearSelection],
  );

  return (
    <div className="w-full flex items-center justify-between px-4 py-2 gap-2 min-h-[52px] md:py-2.5 border-b border-border-light flex-shrink-0 bg-notelist md:bg-transparent md:px-2">
      <Button
        variant="outline"
        size="header"
        onClick={clearSelection}
        title={t("noteList.clearSelection")}
      >
        <Icon name="close" size={14} />
      </Button>
      <ToolbarPill size="header">
        <span className="text-sm font-semibold text-muted whitespace-nowrap min-w-8 pl-1.5 text-center">
          {count}
        </span>

        <ToolbarSeparator />

        {variant === "trash" ? (
          <>
            {/* Restore */}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleRestore}
              title={t("bulkActions.restoreSelected")}
            >
              <Icon name="restore" size={14} />
            </Button>

            <ToolbarSeparator />

            {/* Delete permanently */}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleDeletePermanently}
              title={t("bulkActions.deletePermanently")}
              className="text-destructive hover:text-destructive"
            >
              <Icon name="trash" size={14} />
            </Button>
          </>
        ) : variant === "archive" ? (
          <>
            {/* Unarchive */}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleUnarchive}
              title={t("bulkActions.unarchiveSelected")}
            >
              <Icon name="unarchive" size={14} />
            </Button>

            <ToolbarSeparator />

            {/* Move to trash */}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleDelete}
              title={t("bulkActions.moveToTrash")}
              className="text-destructive hover:text-destructive"
            >
              <Icon name="trash" size={14} />
            </Button>
          </>
        ) : (
          <>
            {/* Pin */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" title={t("bulkActions.pin")}>
                  <Icon name="pin" size={14} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" side="bottom">
                <DropdownMenuItem onClick={handlePin}>
                  <Icon name="pin" size={14} />
                  {t("bulkActions.pinSelected")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleUnpin}>
                  <Icon name="unpin" size={14} />
                  {t("bulkActions.unpinSelected")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Tag */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" title={t("bulkActions.addTag")}>
                  <Icon name="tag" size={14} />
                </Button>
              </PopoverTrigger>
              <PopoverContent side="bottom" align="center" className="w-56 p-0">
                <div className="relative overflow-y-auto">
                  <div className="p-2 sticky top-0">
                    <input
                      type="text"
                      placeholder={t("bulkActions.filterTags")}
                      value={tagFilterText}
                      onChange={(e) => setTagFilterText(e.target.value)}
                      className="w-full text-sm px-2 py-1.5 rounded-full border border-border-light bg-canvas text-ink placeholder:text-placeholder outline-none focus:border-accent font-chrome"
                      autoFocus
                    />
                  </div>
                  <div className="max-h-56 px-1 pb-1">
                    {filteredTags.length === 0 ? (
                      <p className="text-xs text-muted px-2 py-2 font-chrome">
                        {t("bulkActions.noTagsFound")}
                      </p>
                    ) : (
                      filteredTags.map((tag) => (
                        <button
                          key={tag.id}
                          onClick={() => handleAddTag(tag.id)}
                          className="w-full text-left text-sm px-2 py-1.5 rounded-full text-ink hover:bg-hover transition-colors font-chrome cursor-pointer"
                        >
                          #{tag.name}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              </PopoverContent>
            </Popover>

            {/* Export */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" title={t("bulkActions.exportSelected")}>
                  <Icon name="download" size={14} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" side="bottom">
                <DropdownMenuItem onClick={() => handleExport("markdown")}>
                  {t("noteActions.exportMarkdown")}
                  {!isPro && <ProTag className="ml-auto" />}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport("txt")}>
                  {t("noteActions.exportPlainText")}
                  {!isPro && <ProTag className="ml-auto" />}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport("textpack")}>
                  {t("noteActions.exportTextpack")}
                  {!isPro && <ProTag className="ml-auto" />}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleExport("html")}>
                  {t("noteActions.exportHtml")}
                  {!isPro && <ProTag className="ml-auto" />}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport("rtf")}>
                  {t("noteActions.exportRtf")}
                  {!isPro && <ProTag className="ml-auto" />}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport("epub")}>
                  {t("noteActions.exportEpub")}
                  {!isPro && <ProTag className="ml-auto" />}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <ToolbarSeparator />

            {/* Archive */}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleArchive}
              title={t("bulkActions.archiveSelected")}
            >
              <Icon name="archive" size={14} />
            </Button>

            {/* Move to trash */}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleDelete}
              title={t("bulkActions.moveToTrash")}
              className="text-destructive hover:text-destructive"
            >
              <Icon name="trash" size={14} />
            </Button>
          </>
        )}
      </ToolbarPill>
      <Dialog alert open={leaveCount > 0} onOpenChange={(o) => !o && setLeaveCount(0)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              <Icon name="sign-out" size={14} />
              {t("sharing.leaveSelectedTitle")}
            </DialogTitle>
          </DialogHeader>
          <p className="px-4 py-4 text-sm text-ink-secondary">
            {t("sharing.leaveSelectedBody", { count: leaveCount })}
          </p>
          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={() => setLeaveCount(0)}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={() => {
                setLeaveCount(0);
                doDelete();
              }}
            >
              {t("sharing.leave")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
